import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

function BatchPayrollDialog({
  open,
  onClose,
  onSubmit,
  formData,
  onInputChange,
  formErrors,
  isSubmitting = false,
}) {
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Create Batch Payroll
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Generate payroll records for all active employees with their default salary values
        </Typography>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.month}>
                <InputLabel>Month</InputLabel>
                <Select
                  value={formData.month || ''}
                  label="Month"
                  onChange={(e) => onInputChange('month', e.target.value)}
                  required
                >
                  {MONTHS.map((month) => (
                    <MenuItem key={month} value={month}>
                      {month}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.month && (
                  <Typography variant="caption" color="error">
                    {formErrors.month}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth error={!!formErrors.year}>
                <InputLabel>Year</InputLabel>
                <Select
                  value={formData.year || ''}
                  label="Year"
                  onChange={(e) => onInputChange('year', e.target.value)}
                  required
                >
                  {years.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.year && (
                  <Typography variant="caption" color="error">
                    {formErrors.year}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: (theme) => theme.palette.grey[50],
                  borderRadius: 1,
                  border: 1,
                  borderColor: (theme) => theme.palette.grey[300],
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  ℹ️ Batch Payroll Information
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  • Payroll records will be created for all active employees
                  <br />
                  • Default salary values from employee records will be used
                  <br />
                  • All payroll records will have "Pending" status
                  <br />
                  • Employees with existing payroll for the selected month/year will be skipped
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Creating...' : 'Create Batch Payroll'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

BatchPayrollDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formData: PropTypes.shape({
    month: PropTypes.string,
    year: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onInputChange: PropTypes.func.isRequired,
  formErrors: PropTypes.shape({
    month: PropTypes.string,
    year: PropTypes.string,
  }),
  isSubmitting: PropTypes.bool,
};

BatchPayrollDialog.defaultProps = {
  formErrors: {},
  isSubmitting: false,
};

export default BatchPayrollDialog;